import { t } from "@lingui/macro";
import { CheckCircle } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Logo } from "@/client/components/logo";

const TRANSITION_STEPS = [
  () => t`Creating your account`,
  () => t`Personalizing your dashboard`,
  () => t`Almost ready`,
] as const;

type Props = {
  name?: string;
  durationMs?: number;
  onDone?: () => void;
};

export const WelcomeOverlay = ({ name, durationMs = 1200, onDone }: Props) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const stepInterval = durationMs / TRANSITION_STEPS.length;
    const timer = setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, TRANSITION_STEPS.length - 1));
    }, stepInterval);
    return () => clearInterval(timer);
  }, [durationMs]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, durationMs);
    return () => clearTimeout(t);
  }, [durationMs, onDone]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="welcome-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.05 }}
            className="flex w-full max-w-sm flex-col items-center gap-y-6 px-8 text-center"
          >
            <Logo size={56} />

            <div className="space-y-1.5">
              <h2 className="text-2xl font-semibold tracking-tight">
                {name ? t`Welcome, ${name}!` : t`Welcome to CareerBldr!`}
              </h2>
              <p className="text-sm text-foreground/60">{t`We're getting your account ready.`}</p>
            </div>

            <div className="w-full space-y-3">
              <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
                <motion.div
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: durationMs / 1000, ease: "easeInOut" }}
                  className="h-full rounded-full bg-primary"
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={stepIndex}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center justify-center gap-x-2 text-sm text-foreground/70"
                >
                  <CheckCircle size={14} weight="fill" className="text-primary" />
                  <span>{TRANSITION_STEPS[stepIndex]()}…</span>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
